<template>
    <div>
        <Pane>
            <span>票据详情</span>
        </Pane>
        <div style="padding:16px">
            <!-- 第一块：票据基本信息 -->
            <a-card bordered>
                <template #title>
                    <a-space>
                        <a-icon type="read" />
                        <span>票据基本信息</span>
                    </a-space>
                </template>
                <template #extra>
                    <a-space>
                        <a-button @click="handleEdit">编辑</a-button>
                        <a-button type="primary" @click="handleUse">票据使用</a-button>
                    </a-space>
                </template>
                <!-- flex布局：左边descriptions，右侧竖排金额，还原截图布局 -->
                <div style="display:flex;align-items:flex-start;gap:24px;">
                    <a-descriptions :column="2" bordered style="flex:1;">
                        <a-descriptions-item label="票据号码">{{ billDetail.billNo || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="票据类型">{{ billDetail.billTypeName || '-' }}</a-descriptions-item>

                        <a-descriptions-item label="票据状态">
                            <a-tag :color="statusColor">{{ billDetail.billStatusName || '-' }}</a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="所属银行">{{ bankNameEcho }}</a-descriptions-item>

                        <a-descriptions-item label="出票人">{{ billDetail.drawer || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="付款行">{{ payBankEcho }}</a-descriptions-item>

                        <a-descriptions-item label="下手背书人">{{ billDetail.nextEndorser || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="上手背书人">{{ billDetail.prevEndorser || '-' }}</a-descriptions-item>

                        <a-descriptions-item label="背书金额">¥{{ formatAmount(billDetail.endorseAmount) }}</a-descriptions-item>
                        <a-descriptions-item label="到期托收/贴现金额">-</a-descriptions-item>

                        <a-descriptions-item label="出票日期">{{ billDetail.issueDate || '-' }}</a-descriptions-item>
                        <a-descriptions-item label="到期日期">{{ billDetail.dueDate || '-' }}</a-descriptions-item>
                    </a-descriptions>
                    <!-- 右侧垂直三块金额，和截图一模一样 -->
                    <div style="min-width:20%;">
                        <div style="font-size:17px;margin-bottom:12px;">
                            <strong>{{ formatAmount(billDetail.amount) }} 元</strong>
                            <div>票面金额</div>
                        </div>
                        <div style="font-size:17px;margin-bottom:12px;">
                            <strong>{{ formatAmount(billDetail.usedAmount) }} 元</strong>
                            <div>已使用金额</div>
                        </div>
                        <div style="font-size:17px;">
                            <strong style="color:#1890ff;">{{ formatAmount(billDetail.inHandAmount) }} 元</strong>
                            <div>当前余额</div>
                        </div>
                    </div>
                </div>
            </a-card>

            <div style="margin-top:16px"></div>

            <!-- 第二块：票据流转记录 时间线 -->
            <a-card bordered>
                <template #title>
                    <a-space>
                        <a-icon type="history" />
                        <span>票据流转记录</span>
                    </a-space>
                </template>
                <a-timeline v-if="flowList.length">
                    <a-timeline-item v-for="(item, idx) in flowList" :key="idx" :color="idx === 0 ? 'blue' : 'gray'">
                        <div style="font-size: 15px;"><strong>{{ item.date }} <span style="color: rgb(24, 144, 255)">{{ item.title }}</span></strong></div>
                        <div style="color:#666;font-size:13px;margin-top:4px;">{{ item.desc }}</div>
                    </a-timeline-item>
                </a-timeline>
                <a-empty v-else description="暂无流转记录" />
            </a-card>

            <div style="margin-top:16px"></div>

            <!-- 第三块：票据附件 -->
            <a-card bordered>
                <template #title>
                    <a-space>
                        <a-icon type="paper-clip" />
                        <span>票据附件</span>
                    </a-space>
                </template>
                <a-list :data-source="fileList" :locale="{ emptyText: '暂无附件' }">
                    <template slot="renderItem" slot-scope="item">
                        <a-list-item>
                            <template slot="actions">
                                <a @click="handlePreview(item)">预览</a>
                                <a @click="handleDownload(item)">下载</a>
                            </template>
                            <a-list-item-meta>
                                <template slot="avatar">
                                    <a-icon :type="fileIcon(item)" :style="{ fontSize: '20px', color: iconColor(item) }" />
                                </template>
                                <template slot="title">{{ item.name }}</template>
                            </a-list-item-meta>
                        </a-list-item>
                    </template>
                </a-list>
            </a-card>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { fetchBillDetail } from "@/api/finance/bill-library";

export default {
    name: "BillDetail",
    data() {
        return {
            billId: this.$route.query.id,
            billDetail: {},
            flowList: [],
            fileList: [],
            statusColorMap: {
                HELD: "green",
                ENDORSED: "blue",
                DISCOUNTED: "orange",
                COLLECTED: "cyan",
                VOIDED: "red",
                PART_USED: "green" // 部分使用状态绿色，截图是绿色小圆点
            },
        };
    },
    computed: {
        ...mapGetters("setting", ["findSingleDictName"]),
        bankNameEcho() {
            return this.findSingleDictName("bank", this.billDetail.bankName) || this.billDetail.bankName || '-';
        },
        payBankEcho() {
            return this.findSingleDictName("bank", this.billDetail.payBank) || this.billDetail.payBank || '-';
        },
        statusColor() {
            return this.statusColorMap[this.billDetail.billStatus] || 'blue';
        },
    },
    mounted() {
        this.loadDetail();
    },
    methods: {
        ...mapMutations("tabs", ["setTabList"]),
        formatAmount(value) {
            return value != null && value !== '' ? Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
        },
        async loadDetail() {
            try {
                const res = await fetchBillDetail(this.billId) || {};
                this.billDetail = res;
                // ✅后端流转记录是 useRecordList，组装flowList用于时间轴渲染
                this.flowList = (res.useRecordList || []).map(rec => {
                    let descText = `金额：${this.formatAmount(rec.useAmount)}元，经办人：${rec.operatorName || '-'}`;
                    if(rec.nextEndorsee){
                        descText += `，背书对象：${rec.nextEndorsee}`;
                    }
                    if(rec.discountBank){
                        descText += `，贴现银行：${rec.discountBank}`;
                    }
                    if(rec.collectionBank){
                        descText += `，托收银行：${rec.collectionBank}`;
                    }
                    descText += `，余额：${this.formatAmount(rec.afterBalance)}元`;
                    return {
                        date: rec.useDate,
                        title: rec.useTypeName,
                        desc: descText
                    }
                })
                // ✅后端附件字段 attachmentFiles
                this.fileList = res.attachmentFiles || [];
            } catch (e) {
                console.error("加载票据详情失败", e);
            }
        },
        handleEdit() {
            this.$router.push(this.$route.path.replace("/bill-detail", "/bill-form") + "?id=" + this.billId);
        },
        handleUse() {
            const path = `/finance/transaction/detail/bill-use?id=${this.billId}`;
            const basePath = `/finance/transaction/detail/bill-use`;
            const tabList = [...this.$store.state.tabs.tabList];
            if (!tabList.some(t => t.path === basePath)) {
                tabList.push({ name: '票据使用', path: basePath, fullPath: path });
                this.setTabList(tabList);
            }
            this.$router.push(path).catch(() => {});
        },
        handlePreview(file) {
            const url = file.completePath || file.url;
            if (url) {
                window.open(url);
            }
        },
        handleDownload(file) {
            const url = file.completePath || file.url;
            if (url) {
                const a = document.createElement("a");
                a.href = url;
                a.download = file.name || "";
                a.click();
            }
        },
        fileIcon(file) {
            const ext = (file.ext || file.name.split('.').pop() || '').toLowerCase();
            if (ext === 'pdf') return 'file-pdf';
            if (['xls', 'xlsx'].includes(ext)) return 'file-excel';
            if (['doc', 'docx'].includes(ext)) return 'file-word';
            if (['png', 'jpg', 'jpeg', 'gif', 'bmp'].includes(ext)) return 'file-image';
            if (['zip', 'rar', '7z'].includes(ext)) return 'file-zip';
            return 'file-text';
        },
        iconColor(file) {
            const ext = (file.ext || file.name.split('.').pop() || '').toLowerCase();
            if (ext === 'pdf') return '#f5222d';
            if (['xls', 'xlsx'].includes(ext)) return '#52c41a';
            if (['doc', 'docx'].includes(ext)) return '#1890ff';
            if (['png', 'jpg', 'jpeg', 'gif', 'bmp'].includes(ext)) return '#fa8c16';
            return '#8c8c8c';
        },
    },
};
</script>
